import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, Textarea, FormControl,
  FormLabel, Box, Text, Badge, Checkbox, useToast, Flex, IconButton, Wrap, WrapItem,
  List, ListItem, Divider, Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiMapPin, FiLink, FiX, FiBell, FiUserCheck, FiEyeOff, FiEye, FiBookmark, FiCheck, FiCheckCircle, FiCalendar, FiRefreshCw, FiCopy } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../contexts/AuthContext';

const ENTRY_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'meeting_video', label: 'Meeting - Video' },
  { value: 'meeting_in_person', label: 'Meeting - In Person' },
  { value: 'studio_work', label: 'Studio Work' },
  { value: 'location_work', label: 'Location Work' },
  { value: 'annual_leave', label: 'Annual Leave' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'sales', label: 'Sales' },
  { value: 'admin', label: 'Admin' },
];

const TYPE_COLORS = {
  meeting_video: 'blue', meeting_in_person: 'purple', studio_work: 'teal',
  location_work: 'orange', annual_leave: 'green', maintenance: 'gray',
  sales: 'cyan', admin: 'yellow',
};

function PersonPicker({ label, selected, onAdd, onRemove, searchEndpoint, nameKey = 'display_name', colorScheme = 'purple' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  const search = async (q) => {
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    try {
      const { data } = await api.get(`${searchEndpoint}?q=${encodeURIComponent(q)}`);
      setResults(data.filter(r => !selected.find(s => s.id === r.id)));
      setOpen(true);
    } catch(e) {}
  };

  return (
    <Box>
      {selected.length > 0 && (
        <Wrap spacing={1} mb={1}>
          {selected.map(p => (
            <WrapItem key={p.id}>
              <Badge colorScheme={colorScheme} fontSize="9px" display="flex" alignItems="center" gap={1}
                cursor="pointer" onClick={() => onRemove(p.id)}>
                {nameKey === 'display_name' ? (p.display_name || p.username) : `${p.first_name} ${p.last_name || ''}`}
                <FiX size={8} />
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}
      <Box position="relative">
        <Input size="xs" value={query} placeholder={`Add ${label}...`}
          onChange={(e) => { setQuery(e.target.value); clearTimeout(debounce.current); debounce.current = setTimeout(() => search(e.target.value), 200); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {open && results.length > 0 && (
          <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
            borderRadius="md" boxShadow="lg" zIndex={200} maxH="120px" overflowY="auto">
            <List spacing={0}>
              {results.map(r => (
                <ListItem key={r.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                  onClick={() => { onAdd(r); setQuery(''); setOpen(false); }}>
                  <Text fontWeight="500">
                    {nameKey === 'display_name' ? (r.display_name || r.username) : `${r.first_name} ${r.last_name || ''}`}
                  </Text>
                  {r.company_name && <Text fontSize="9px" color="gray.500">{r.company_name}</Text>}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// User picker loads all users (small list)
function UserPicker({ selected, onAdd, onRemove }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (loaded) return;
    try { const { data } = await api.get('/calendar/users'); setAllUsers(data); setLoaded(true); } catch(e) {}
  };

  // Compare ids as strings — see comment on the Select onChange below for
  // why: server responses can return ids as either number or UUID string and
  // the equality must be consistent in both shapes.
  const available = allUsers.filter(u => !selected.find(s => String(s.id) === String(u.id)));

  return (
    <Box>
      {selected.length > 0 && (
        <Wrap spacing={1} mb={1}>
          {selected.map(u => (
            <WrapItem key={u.id}>
              <Badge colorScheme="blue" fontSize="9px" display="flex" alignItems="center" gap={1}
                cursor="pointer" onClick={() => onRemove(u.id)}>
                {u.display_name || u.username} <FiX size={8} />
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}
      <Select size="xs" placeholder="Add user..." onFocus={load}
        onChange={(e) => {
          // The DOM Select's value is always a string; the user `id` from the
          // API may be either a number (legacy /calendar/users response) or a
          // UUID string (post-UUID-migration response). Compare as strings so
          // the lookup works either way. Without this, every "Add user"
          // selection silently dropped the user when ids were numeric.
          const picked = e.target.value;
          if (!picked) return;
          const u = allUsers.find(x => String(x.id) === String(picked));
          if (u) onAdd(u);
          e.target.value = '';
        }}>
        {available.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
      </Select>
    </Box>
  );
}

export default function CalendarModal({ projectId, taskId, gridTasks, isOpen, onClose }) {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
    location: '', link: '', priority: 'standard', entry_type: 'standard', all_day: false, contacts: [], users: [],
    alarm_enabled: false, alarm_warnings: [15], recurrence_type: 'none', recurrence_end_date: '', show_in_schedule: true,
    resource_booking_id: null, resource_booking_action: 'none', new_booking_resource_ids: [], new_booking_title: '',
    project_id: projectId, task_id: taskId || null,
  });
  // Project/task pickers (move an entry to a different task or project)
  const [projectsList, setProjectsList] = useState([]);
  const [tasksList, setTasksList] = useState([]);
  const [projQuery, setProjQuery] = useState('');   // project search box text
  const [projOpen, setProjOpen] = useState(false);  // project search dropdown open
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteTitle, setConfirmDeleteTitle] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [attendance, setAttendance] = useState({}); // { entryId: [{ user_id, display_name, attended }] }
  const [expandedAttendance, setExpandedAttendance] = useState(null);
  const [contactAttendance, setContactAttendance] = useState({}); // { entryId: [{contact_id, attended, first_name, last_name}] }
  const [expandedActions, setExpandedActions] = useState(null); // entryId with actions panel open
  const [rescheduleForm, setRescheduleForm] = useState({}); // { entryId: { new_date, new_time, notes } }
  const [savingAction, setSavingAction] = useState(null);
  const { user: authUser } = useAuth();
  const [availableResources, setAvailableResources] = useState([]);
  const [resourceTypeFilter, setResourceTypeFilter] = useState('All');
  const [resourceSearch, setResourceSearch] = useState('');
  const [linkedBookings, setLinkedBookings] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);

  const fetchEntries = useCallback(async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
    // Fetch available resources and existing bookings for this task
    api.get('/resources').then(r => setAvailableResources(r.data || [])).catch(() => {});
    if (taskId) api.get(`/resource-bookings?task_id=${taskId}`).then(r => setExistingBookings(r.data || [])).catch(() => {});
      const { data } = await api.get(`/calendar?project_id=${projectId}${taskParam}`);
      setEntries(data);
    } catch(e) {}
  }, [projectId, taskId]);

  const fetchAttendance = async (entryId) => {
    try {
      const { data } = await api.get(`/calendar/${entryId}/attendance`);
      setAttendance(prev => ({ ...prev, [entryId]: data }));
    } catch(e) {}
  };

  const toggleAttendance = async (entryId, userId, attended) => {
    try {
      const { data } = await api.post(`/calendar/${entryId}/attendance`, { user_id: userId, attended });
      setAttendance(prev => ({ ...prev, [entryId]: data }));
    } catch(e) {}
  };

  const fetchContactAttendance = async (entryId) => {
    try {
      const { data } = await api.get(`/calendar/${entryId}/contact-attendance`);
      setContactAttendance(prev => ({ ...prev, [entryId]: data }));
    } catch(e) {}
  };

  const toggleContactAttendance = async (entryId, contactId, attended) => {
    try {
      const { data } = await api.post(`/calendar/${entryId}/contact-attendance`, { contact_id: contactId, attended });
      setContactAttendance(prev => ({ ...prev, [entryId]: data }));
    } catch(e) {}
  };

  const markComplete = async (entry, complete) => {
    setSavingAction(entry.id);
    try {
      await api.patch(`/calendar/${entry.id}/complete`, { is_complete: complete });
      await fetchEntries();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSavingAction(null);
  };

  const doReschedule = async (entryId) => {
    const rf = rescheduleForm[entryId] || {};
    if (!rf.new_date) return toast({ title: 'Please pick a new date', status: 'warning', duration: 2000 });
    setSavingAction(entryId);
    try {
      await api.patch(`/calendar/${entryId}/reschedule`, { new_date: rf.new_date, new_time: rf.new_time || null, notes: rf.notes || null });
      await fetchEntries();
      setExpandedActions(null);
      toast({ title: 'Event rescheduled', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSavingAction(null);
  };

  useEffect(() => {
    if (isOpen && projectId) { fetchEntries(); setShowForm(false); setEditingId(null); }
  }, [isOpen, projectId, fetchEntries]);

  // Project list for the "move to another project" picker
  useEffect(() => {
    if (!isOpen) return;
    api.get('/projects').then(r => setProjectsList(Array.isArray(r.data) ? r.data : [])).catch(() => setProjectsList([]));
  }, [isOpen]);

  // Tasks for whichever project the form currently targets
  useEffect(() => {
    const pid = form.project_id || projectId;
    if (!isOpen || !pid) { setTasksList([]); return; }
    // For the project the modal opened on (the current / cloned-from project),
    // prefer the tasks already loaded in the grid — they are guaranteed to be
    // present, so cloning can always pick a task from the source project even
    // if the /tasks fetch is unavailable. Other projects use /tasks.
    if (String(pid) === String(projectId) && Array.isArray(gridTasks) && gridTasks.length) {
      setTasksList(gridTasks);
      return;
    }
    api.get(`/tasks?project_id=${encodeURIComponent(pid)}`).then(r => setTasksList(Array.isArray(r.data) ? r.data : [])).catch(() => setTasksList([]));
  }, [isOpen, form.project_id, projectId, gridTasks]);

  const resetForm = () => setForm({
    title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
    location: '', link: '', priority: 'standard', entry_type: 'standard', all_day: false, contacts: [], users: [],
    alarm_enabled: false, alarm_warnings: [15], recurrence_type: 'none', recurrence_end_date: '', show_in_schedule: true,
    resource_booking_id: null, resource_booking_action: 'none', new_booking_resource_ids: [], new_booking_title: '',
    project_id: projectId, task_id: taskId || null,
  });

  const openNew = () => { resetForm(); setEditingId(null); setShowForm(true); };

  const openEdit = async (entry) => {
    let alarmWarnings = [15];
    try { const { data } = await api.get(`/calendar/${entry.id}/alarms`); if (data.length > 0) alarmWarnings = data.map(a => a.minutes_before); } catch(e) {}
    setForm({
      title: entry.title || '', description: entry.description || '',
      start_date: entry.start_date ? entry.start_date.split('T')[0] : '',
      start_time: entry.start_time ? entry.start_time.slice(0, 5) : '',
      end_date: entry.end_date ? entry.end_date.split('T')[0] : '',
      end_time: entry.end_time ? entry.end_time.slice(0, 5) : '',
      location: entry.location || '', link: entry.link || '',
      priority: entry.priority || 'standard',
      entry_type: entry.entry_type || 'standard',
      all_day: !!entry.all_day,
      contacts: entry.contacts || [],
      users: entry.users || [],
      alarm_enabled: !!entry.alarm_enabled,
      alarm_warnings: alarmWarnings,
      recurrence_type: entry.recurrence_type || 'none',
      recurrence_end_date: entry.recurrence_end_date ? entry.recurrence_end_date.split('T')[0] : '',
      show_in_schedule: entry.show_in_schedule !== false,
      project_id: entry.project_id || projectId,
      task_id: entry.task_id || null,
      // Load linked resource booking if any
      resource_booking_action: 'none', resource_booking_id: null, new_booking_resource_ids: [], new_booking_title: '',
    });
    setEditingId(entry.id); setShowForm(true);
    // Fetch resource bookings linked to this calendar entry
    try {
      const { data: linked } = await api.get(`/resource-bookings?calendar_entry_id=${entry.id}`);
      if (linked && linked.length > 0) {
        const booking = linked[0];
        setForm(p => ({
          ...p,
          resource_booking_action: 'link_existing',
          resource_booking_id: booking.id,
          new_booking_resource_ids: (booking.resources || []).map(r => r.id),
        }));
        setLinkedBookings(linked);
      }
    } catch(e) {}
  };

  // Clone: open the New Entry form pre-filled exactly like the source entry,
  // but reset to today's date and 00:00 times, as a brand-new entry.
  const openClone = async (entry) => {
    const today = new Date().toISOString().split('T')[0];
    let alarmWarnings = [15];
    try { const { data } = await api.get(`/calendar/${entry.id}/alarms`); if (data.length > 0) alarmWarnings = data.map(a => a.minutes_before); } catch(e) {}
    setEditingId(null);
    setLinkedBookings([]);
    setForm({
      title: entry.title || '', description: entry.description || '',
      start_date: today, start_time: '00:00',
      end_date: today, end_time: '00:00',
      location: entry.location || '', link: entry.link || '',
      priority: entry.priority || 'standard',
      entry_type: entry.entry_type || 'standard',
      all_day: !!entry.all_day,
      contacts: entry.contacts || [],
      users: entry.users || [],
      alarm_enabled: !!entry.alarm_enabled,
      alarm_warnings: alarmWarnings,
      recurrence_type: entry.recurrence_type || 'none',
      recurrence_end_date: entry.recurrence_end_date ? entry.recurrence_end_date.split('T')[0] : '',
      show_in_schedule: entry.show_in_schedule !== false,
      project_id: entry.project_id || projectId,
      task_id: entry.task_id || null,
      resource_booking_action: 'none', resource_booking_id: null, new_booking_resource_ids: [], new_booking_title: '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.start_date) { toast({ title: 'Title and start date required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = {
        project_id: form.project_id || projectId, task_id: form.task_id || null, title: form.title, description: form.description,
        start_date: form.start_date, start_time: form.all_day ? null : (form.start_time || null),
        end_date: form.all_day ? form.start_date : (form.end_date || null),
        end_time: form.all_day ? null : (form.end_time || null),
        location: form.location || null, link: form.link || null,
        priority: form.priority, entry_type: form.entry_type, all_day: form.all_day,
        contact_ids: form.contacts.map(c => c.id),
        user_ids: form.users.map(u => u.id),
        alarm_enabled: form.alarm_enabled,
        alarm_warnings: form.alarm_enabled ? form.alarm_warnings : [],
        recurrence_type: form.recurrence_type || 'none',
        recurrence_end_date: form.recurrence_end_date || null,
        show_in_schedule: form.show_in_schedule,
      };
      if (editingId) { await api.put(`/calendar/${editingId}`, payload); }
      else { await api.post('/calendar', payload); }
      const savedEntryId = editingId || null; // will be the new entry id if POST
      toast({ title: editingId ? 'Updated' : 'Added', status: 'success', duration: 1500 });
      // Create linked resource booking if requested
      if (form.resource_booking_action === 'create_new' && form.new_booking_resource_ids.length > 0) {
        try {
          const ceRes = await api.get(`/calendar?project_id=${form.project_id || projectId}${form.task_id ? '&task_id=' + form.task_id : ''}`);
          const entries = Array.isArray(ceRes.data) ? ceRes.data : [];
          const latest = entries.find(e => e.title === form.title && e.start_date?.split('T')[0] === form.start_date) || entries[entries.length - 1];
          if (latest) {
            await api.post('/resource-bookings', {
              project_id: form.project_id || projectId,
              task_id: form.task_id || null,
              calendar_entry_id: latest.id,
              title: form.new_booking_title || form.title,
              start_date: form.start_date,
              start_time: form.all_day ? null : (form.start_time || null),
              end_date: form.end_date || form.start_date,
              end_time: form.all_day ? null : (form.end_time || null),
              resource_ids: form.new_booking_resource_ids,
              status: 'confirmed',
            });
          }
        } catch(e) { console.error('Resource booking link failed', e); }
      }
      await fetchEntries(); setShowForm(false); resetForm(); setEditingId(null);
    } catch(e) { toast({ title: 'Failed to save', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = (id, title) => {
    setConfirmDeleteId(id);
    setConfirmDeleteTitle(title || 'this calendar entry');
  };

  const doDelete = async () => {
    if (!confirmDeleteId) return;
    try { await api.delete(`/calendar/${confirmDeleteId}`); await fetchEntries(); toast({ title: 'Deleted', status: 'info', duration: 1500 }); }
    catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setConfirmDeleteId(null); }
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const isPastEntry = (e) => e.start_date && e.start_date.split('T')[0] < todayStr;
  const getEntryBg = (entry, isHidden) => {
    if (isHidden) return 'orange.50';
    if (isPastEntry(entry)) return 'gray.50';
    return 'white';
  };

  const formatDate = (d, t) => {
    if (!d) return '';
    const dateStr = typeof d === 'string' ? d.split('T')[0] : d;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return d;
    const ds = `${parts[2]}/${parts[1]}/${parts[0]}`;
    return t ? `${ds} ${t.slice(0, 5)}` : ds;
  };

  const getTypeLabel = (v) => ENTRY_TYPES.find(t => t.value === v)?.label || v;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex align="center" justify="space-between" pr={8}>
            <Box>
              Calendar
              {taskId && (
                <Badge ml={2} colorScheme="blue" fontSize="9px" verticalAlign="middle">
                  Task entries only
                </Badge>
              )}
            </Box>
            <Button size="xs" leftIcon={<FiPlus />} onClick={showForm && !editingId ? () => setShowForm(false) : openNew}
              variant={showForm && !editingId ? 'solid' : 'outline'} colorScheme="teal">
              {showForm && !editingId ? 'Cancel' : 'New Entry'}
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg={editingId ? 'orange.50' : 'teal.50'} p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}>
                    <FormLabel fontSize="xs" mb={0.5}>Title *</FormLabel>
                    <Input size="xs" value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                    <Select size="xs" value={form.entry_type} onChange={(e) => setForm(p => ({ ...p, entry_type: e.target.value }))}>
                      {ENTRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                  </FormControl>
                </HStack>
                <HStack w="100%" spacing={2} align="flex-start">
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Project</FormLabel>
                    {(() => {
                      const selProj = projectsList.find(pr => String(pr.id) === String(form.project_id));
                      const selName = selProj ? (selProj.project_name || selProj.name || '') : '';
                      const q = projQuery.trim().toLowerCase();
                      const filtered = (q
                        ? projectsList.filter(pr => (pr.project_name || pr.name || '').toLowerCase().includes(q))
                        : projectsList).slice(0, 50);
                      const pickProject = (pr) => {
                        setForm(p => ({ ...p, project_id: pr.id, task_id: null }));
                        setProjQuery(''); setProjOpen(false);
                      };
                      return (
                        <Box position="relative">
                          <Input size="xs" placeholder="Search project…"
                            value={projOpen ? projQuery : selName}
                            onFocus={() => { setProjOpen(true); setProjQuery(''); }}
                            onChange={(e) => { setProjQuery(e.target.value); setProjOpen(true); }}
                            onBlur={() => setTimeout(() => setProjOpen(false), 150)} />
                          {projOpen && (
                            <Box position="absolute" top="100%" left={0} right={0} mt={1} bg="white" border="1px"
                              borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={2000}
                              maxH="200px" overflowY="auto">
                              {filtered.length === 0 ? (
                                <Text fontSize="11px" color="gray.400" px={2} py={1}>No matching projects</Text>
                              ) : (
                                <List spacing={0}>
                                  {filtered.map(pr => (
                                    <ListItem key={pr.id} px={2} py={1} fontSize="11px" cursor="pointer"
                                      bg={String(pr.id) === String(form.project_id) ? 'blue.50' : 'white'}
                                      _hover={{ bg: 'blue.50' }}
                                      onMouseDown={(e) => { e.preventDefault(); pickProject(pr); }}>
                                      {pr.project_name || pr.name || 'Untitled project'}
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Task</FormLabel>
                    <Select size="xs" value={form.task_id || ''}
                      onChange={(e) => setForm(p => ({ ...p, task_id: e.target.value || null }))}>
                      <option value="">— No task (project level) —</option>
                      {tasksList.map(t => (
                        <option key={t.id} value={t.id}>{t.task_title || t.title || 'Untitled task'}</option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>
                <VStack w="100%" spacing={2} align="stretch">
                  {/* Row 1: Start Date | Start Time | All Day */}
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="xs" mb={0.5}>Start Date *</FormLabel>
                      <Input size="xs" type="date" value={form.start_date} onChange={(e) => {
                        const v = e.target.value;
                        setForm(p => ({ ...p, start_date: v, end_date: v }));
                      }} />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="xs" mb={0.5}>Start Time</FormLabel>
                      <Input size="xs" type="time" value={form.all_day ? '' : form.start_time}
                        isDisabled={form.all_day} opacity={form.all_day ? 0.4 : 1}
                        onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} />
                    </FormControl>
                    <FormControl flex={0} minW="75px" pt={4}>
                      <Checkbox size="sm" isChecked={form.all_day}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm(p => ({
                            ...p, all_day: checked,
                            ...(checked ? { start_time: '', end_time: '', end_date: p.start_date || '' } : {}),
                          }));
                        }}>
                        <Text fontSize="10px">All Day</Text>
                      </Checkbox>
                    </FormControl>
                  </HStack>
                  {/* Row 2: End Date | End Time | spacer */}
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="xs" mb={0.5}>End Date</FormLabel>
                      <Input size="xs" type="date" value={form.end_date} isDisabled={form.all_day}
                        opacity={form.all_day ? 0.4 : 1}
                        onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value }))} />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="xs" mb={0.5}>End Time</FormLabel>
                      <Input size="xs" type="time" value={form.all_day ? '' : form.end_time}
                        isDisabled={form.all_day} opacity={form.all_day ? 0.4 : 1}
                        onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} />
                    </FormControl>
                    <Box flex={0} minW="75px" />
                  </HStack>
                </VStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Textarea size="xs" rows={2} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} />
                </FormControl>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Location</FormLabel>
                    <Input size="xs" value={form.location} placeholder="Address or place" onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Link</FormLabel>
                    <Input size="xs" value={form.link} placeholder="https://..." onChange={(e) => setForm(p => ({ ...p, link: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Priority</FormLabel>
                    <Select size="xs" value={form.priority} onChange={(e) => setForm(p => ({ ...p, priority: e.target.value }))}>
                      <option value="critical">Critical</option>
                      <option value="important">Important</option>
                      <option value="standard">Standard</option>
                      <option value="save_the_date">Save the Date</option>
                      <option value="not_important">Not Important</option>
                    </Select>
                  </FormControl>
                </HStack>
                <HStack w="100%" spacing={2} align="flex-start">
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Contacts</FormLabel>
                    <PersonPicker label="contact" selected={form.contacts}
                      searchEndpoint="/contacts/search" nameKey="first_name" colorScheme="purple"
                      onAdd={(ct) => setForm(p => ({ ...p, contacts: [...p.contacts, ct] }))}
                      onRemove={(id) => setForm(p => ({ ...p, contacts: p.contacts.filter(c => String(c.id) !== String(id)) }))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Team Members</FormLabel>
                    <UserPicker selected={form.users}
                      onAdd={(u) => setForm(p => ({ ...p, users: [...p.users, u] }))}
                      onRemove={(id) => setForm(p => ({ ...p, users: p.users.filter(u => String(u.id) !== String(id)) }))} />
                  </FormControl>
                </HStack>

                {/* Recurrence */}
                <Divider />
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Recurrence</FormLabel>
                    <Select size="xs" value={form.recurrence_type} onChange={e => setForm(p => ({ ...p, recurrence_type: e.target.value }))}>
                      <option value="none">One-Off</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </Select>
                  </FormControl>
                  {form.recurrence_type !== 'none' && (
                    <FormControl flex={1}>
                      <FormLabel fontSize="xs" mb={0.5}>Recurrence End Date</FormLabel>
                      <Input size="xs" type="date" value={form.recurrence_end_date}
                        onChange={e => setForm(p => ({ ...p, recurrence_end_date: e.target.value }))} />
                    </FormControl>
                  )}
                </HStack>

                {/* Alarm */}
                <Divider />
                <Checkbox size="sm" isChecked={form.show_in_schedule}
                  onChange={e => setForm(p => ({ ...p, show_in_schedule: e.target.checked }))}>
                  <Text fontSize="11px">Show in daily schedule</Text>
                </Checkbox>
                {/* ── Resource Booking ── */}
                <Box w="100%" p={2} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                  <HStack mb={2} spacing={1}>
                    <FiBookmark size={12} color="#553C9A" />
                    <Text fontSize="11px" fontWeight="700" color="purple.700">Resource Booking</Text>
                  </HStack>
                  <Select size="xs" mb={2}
                    value={form.resource_booking_action}
                    onChange={e => setForm(p => ({ ...p, resource_booking_action: e.target.value, new_booking_resource_ids: [], new_booking_title: '' }))}>
                    <option value="none">No resource booking</option>
                    <option value="create_new">Create new booking &amp; link</option>
                    <option value="link_existing">Link existing booking</option>
                  </Select>

                  {form.resource_booking_action === 'create_new' && (
                    <VStack spacing={2} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="9px" mb={0.5} color="purple.600">Booking Title (defaults to calendar title)</FormLabel>
                        <Input size="xs" value={form.new_booking_title}
                          onChange={e => setForm(p => ({ ...p, new_booking_title: e.target.value }))}
                          placeholder={form.title || 'Booking title...'} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="9px" mb={0.5} color="purple.600">Resources to book</FormLabel>
                        <HStack spacing={1} mb={1}>
                          {['All','Person','Room','Asset'].map(type => (
                            <Button key={type} size="xs" borderRadius="full"
                              variant={resourceTypeFilter === type ? 'solid' : 'outline'}
                              colorScheme={resourceTypeFilter === type ? 'purple' : 'gray'}
                              onClick={() => setResourceTypeFilter(type)}>
                              {type === 'Person' ? '👤' : type === 'Room' ? '🏠' : type === 'Asset' ? '📦' : ''} {type}
                            </Button>
                          ))}
                        </HStack>
                        <Input size="xs" placeholder="Search resources..." mb={1}
                          value={resourceSearch} onChange={e => setResourceSearch(e.target.value)} />
                        <Box maxH="120px" overflowY="auto" border="1px solid" borderColor="purple.200" borderRadius="md" bg="white" p={1}>
                          {availableResources
                            .filter(r => resourceTypeFilter === 'All' || r.type_name === resourceTypeFilter || (resourceTypeFilter === 'Person' && r.is_person))
                            .filter(r => !resourceSearch || (r.name || '').toLowerCase().includes(resourceSearch.toLowerCase()))
                            .length === 0
                            ? <Text fontSize="9px" color="gray.400" p={1}>No resources match</Text>
                            : availableResources
                              .filter(r => resourceTypeFilter === 'All' || r.type_name === resourceTypeFilter || (resourceTypeFilter === 'Person' && r.is_person))
                              .filter(r => !resourceSearch || (r.name || '').toLowerCase().includes(resourceSearch.toLowerCase()))
                              .map(r => (
                                <Flex key={r.id} align="center" gap={2} py={0.5} px={1}
                                  bg={form.new_booking_resource_ids.includes(r.id) ? 'purple.50' : 'transparent'}
                                  borderRadius="sm" cursor="pointer"
                                  onClick={() => setForm(p => ({
                                    ...p,
                                    new_booking_resource_ids: p.new_booking_resource_ids.includes(r.id)
                                      ? p.new_booking_resource_ids.filter(x => x !== r.id)
                                      : [...p.new_booking_resource_ids, r.id]
                                  }))}>
                                  <Checkbox size="sm" isChecked={form.new_booking_resource_ids.includes(r.id)} onChange={() => {}} pointerEvents="none" colorScheme="purple" />
                                  <Text fontSize="10px">{r.is_person ? '👤' : r.type_name === 'Room' ? '🏠' : '📦'} {r.name}</Text>
                                  {r.type_name && <Badge fontSize="7px" colorScheme="gray">{r.type_name}</Badge>}
                                </Flex>
                              ))
                          }
                        </Box>
                        {form.new_booking_resource_ids.length > 0 && (
                          <Text fontSize="9px" color="purple.600" mt={1}>{form.new_booking_resource_ids.length} resource{form.new_booking_resource_ids.length > 1 ? 's' : ''} selected</Text>
                        )}
                      </FormControl>
                    </VStack>
                  )}

                  {form.resource_booking_action === 'link_existing' && (
                    <Box>
                      <Select size="xs" mb={1}
                        value={form.resource_booking_id || ''}
                        onChange={e => setForm(p => ({ ...p, resource_booking_id: e.target.value || null }))}>
                        <option value="">— Select existing booking —</option>
                        {existingBookings.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.title || b.id} — {b.start_date?.split('T')[0]}{(b.resources||[]).length > 0 ? ` (${b.resources.map(r=>r.name).join(', ')})` : ''}
                          </option>
                        ))}
                      </Select>
                      {linkedBookings.length > 0 && (
                        <Box bg="purple.50" borderRadius="md" p={2} border="1px" borderColor="purple.200">
                          <Text fontSize="9px" color="purple.600" fontWeight="700" mb={1}>Currently linked booking</Text>
                          {linkedBookings.map(b => (
                            <Box key={b.id}>
                              <Text fontSize="10px" fontWeight="600">{b.title}</Text>
                              {(b.resources||[]).length > 0 && (
                                <HStack spacing={1} flexWrap="wrap" mt={0.5}>
                                  {b.resources.map(r => <Badge key={r.id} fontSize="8px" colorScheme="purple">{r.is_person?'👤':r.type_name==='Room'?'🏠':'📦'} {r.name}</Badge>)}
                                </HStack>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Box w="100%">
                  <Checkbox size="sm" isChecked={form.alarm_enabled}
                    onChange={e => setForm(p => ({ ...p, alarm_enabled: e.target.checked }))}>
                    <HStack spacing={1}><FiBell size={12} /><Text fontSize="11px">Set Alarm</Text></HStack>
                  </Checkbox>
                  {!!form.alarm_enabled && (
                    <Box mt={2} pl={5}>
                      <Text fontSize="10px" color="gray.500" mb={1}>Advance warnings (minutes before):</Text>
                      <Flex gap={1} flexWrap="wrap" mb={1}>
                        {form.alarm_warnings.map((mins, i) => (
                          <Badge key={i} colorScheme="orange" fontSize="10px" display="flex" alignItems="center" gap={1}>
                            {mins} min
                            <Box as="span" cursor="pointer" onClick={() => setForm(p => ({
                              ...p, alarm_warnings: p.alarm_warnings.filter((_, j) => j !== i)
                            }))}><FiX size={8} /></Box>
                          </Badge>
                        ))}
                      </Flex>
                      <HStack spacing={1}>
                        <Select size="xs" w="100px" id="alarm-add-select" defaultValue="15">
                          <option value="5">5 min</option>
                          <option value="10">10 min</option>
                          <option value="15">15 min</option>
                          <option value="30">30 min</option>
                          <option value="60">1 hour</option>
                          <option value="120">2 hours</option>
                          <option value="1440">1 day</option>
                        </Select>
                        <Button size="xs" variant="outline" colorScheme="orange" onClick={() => {
                          const sel = document.getElementById('alarm-add-select');
                          const v = parseInt(sel?.value || 15);
                          if (!form.alarm_warnings.includes(v)) setForm(p => ({ ...p, alarm_warnings: [...p.alarm_warnings, v].sort((a,b) => a-b) }));
                        }}>Add</Button>
                      </HStack>
                    </Box>
                  )}
                </Box>

                <HStack w="100%" justify="flex-end">
                  <Button size="xs" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
                  <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                    onClick={handleSave} isLoading={saving}>{editingId ? 'Update' : 'Add Entry'}</Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {entries.length === 0 && !showForm ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No calendar entries yet.</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {entries
                .filter(entry => showHidden || (entry.show_in_schedule !== false && !entry.is_hidden))
                .map(entry => {
                const isEntryHidden = entry.is_hidden || entry.show_in_schedule === false;
                return (
                <Box key={entry.id} p={3} border="1px"
                  borderColor={entry.is_complete ? 'green.300' : isEntryHidden ? 'orange.200' : isPastEntry(entry) ? 'gray.200' : 'gray.100'}
                  borderRadius="md"
                  opacity={isEntryHidden ? 0.5 : isPastEntry(entry) && !entry.is_complete ? 0.8 : 1}
                  bg={entry.is_complete ? 'green.50' : getEntryBg(entry, isEntryHidden)}
                  _hover={{ '& .hide-btn': { opacity: 1 } }}>
                  <Flex justify="space-between" align="flex-start">
                    <Box flex={1}>
                      <Flex align="center" gap={2} flexWrap="wrap">
                        <Text fontSize="12px" fontWeight="600" color={isPastEntry(entry) ? 'gray.500' : 'gray.800'}>{entry.title}</Text>
                        {!!entry.all_day && <Badge fontSize="7px" colorScheme="orange">All Day</Badge>}
                        {entry.entry_type && entry.entry_type !== 'standard' && (
                          <Badge fontSize="7px" colorScheme={TYPE_COLORS[entry.entry_type] || 'gray'}>{getTypeLabel(entry.entry_type)}</Badge>
                        )}
                        {isEntryHidden && <Badge fontSize="7px" colorScheme="orange">Hidden</Badge>}
                        {entry.is_complete && <Badge fontSize="7px" colorScheme="green" variant="solid">✓ Complete</Badge>}
                        {isPastEntry(entry)
                          ? <Badge fontSize="7px" bg="gray.800" color="white">Previous Date</Badge>
                          : entry.status === 'tentative'
                            ? <Badge fontSize="7px" colorScheme="orange">Tentative</Badge>
                            : <Badge fontSize="7px" colorScheme="green">Confirmed</Badge>
                        }
                      </Flex>
                      <HStack fontSize="10px" color="gray.500" spacing={2} mt={0.5}>
                        <Text>{formatDate(entry.start_date, entry.start_time)}</Text>
                        {entry.end_date && <Text>{'→'} {formatDate(entry.end_date, entry.end_time)}</Text>}
                      </HStack>
                      {entry.description && <Text fontSize="11px" color="gray.600" mt={1}>{entry.description}</Text>}
                      <HStack mt={1} spacing={2} flexWrap="wrap">
                        {entry.priority && entry.priority !== 'standard' && (
                          <Badge fontSize="8px"
                            colorScheme={entry.priority === 'critical' ? 'red' : entry.priority === 'important' ? 'orange' : entry.priority === 'save_the_date' ? 'blue' : 'gray'}
                            variant={entry.priority === 'critical' ? 'solid' : 'subtle'}>
                            {entry.priority === 'critical' ? 'CRITICAL' : entry.priority === 'important' ? 'Important' : entry.priority === 'save_the_date' ? 'Save the Date' : 'Not Important'}
                          </Badge>
                        )}
                        {entry.location && (
                          <Badge fontSize="8px" colorScheme="gray" display="flex" alignItems="center" gap={1}>
                            <FiMapPin size={8} /> {entry.location}
                          </Badge>
                        )}
                        {entry.link && (
                          <Badge fontSize="8px" colorScheme="blue" cursor="pointer" display="flex" alignItems="center" gap={1}
                            onClick={() => window.open(entry.link, '_blank')}><FiLink size={8} /> Link</Badge>
                        )}
                        {entry.contacts?.map(ct => (
                          <Badge key={ct.id} fontSize="8px" colorScheme="purple">{ct.first_name} {ct.last_name || ''}</Badge>
                        ))}
                        {entry.users?.map(u => (
                          <Badge key={u.id} fontSize="8px" colorScheme="blue">{u.display_name || u.username}</Badge>
                        ))}
                        {!!entry.alarm_enabled && <Badge fontSize="8px" colorScheme="orange" display="flex" alignItems="center" gap={1}><FiBell size={8} /> Alarm</Badge>}
                        {entry.recurrence_type && entry.recurrence_type !== 'none' && (
                          <Badge fontSize="8px" colorScheme="cyan">{entry.recurrence_type}</Badge>
                        )}
                      </HStack>
                      {/* Attendance */}
                      <Box mt={1}>
                        <Badge fontSize="8px" colorScheme="teal" cursor="pointer" display="flex" alignItems="center" gap={1}
                          onClick={() => {
                            if (expandedAttendance === entry.id) { setExpandedAttendance(null); }
                            else { setExpandedAttendance(entry.id); fetchAttendance(entry.id); }
                          }}>
                          <FiUserCheck size={10} /> Attendance {expandedAttendance === entry.id ? '▾' : '▸'}
                        </Badge>
                        {expandedAttendance === entry.id && (
                          <Box mt={1} pl={2} borderLeft="2px" borderColor="teal.200">
                            {/* Show assigned users */}
                            {(entry.users || []).map(u => {
                              // Compare ids as strings — guards against legacy
                              // numeric / new UUID drift on either side.
                              const att = (attendance[entry.id] || []).find(a => String(a.user_id) === String(u.id));
                              return (
                                <Flex key={u.id} align="center" gap={2} py={0.5}>
                                  <Checkbox size="sm" colorScheme="teal" isChecked={!!att?.attended}
                                    onChange={() => toggleAttendance(entry.id, u.id, !att?.attended)} />
                                  <Text fontSize="10px" color={att?.attended ? 'teal.600' : 'gray.500'}
                                    fontWeight={att?.attended ? '600' : '400'}>
                                    {u.display_name || u.username}
                                  </Text>
                                  {!!att?.attended && <Badge fontSize="7px" colorScheme="teal">✓</Badge>}
                                </Flex>
                              );
                            })}
                            {/* Self-mark if not assigned */}
                            {!(entry.users || []).find(u => String(u.id) === String(authUser?.id)) && (
                              <Flex align="center" gap={2} py={0.5}>
                                <Checkbox size="sm" colorScheme="teal"
                                  isChecked={!!(attendance[entry.id] || []).find(a => String(a.user_id) === String(authUser?.id))?.attended}
                                  onChange={() => {
                                    const att = (attendance[entry.id] || []).find(a => String(a.user_id) === String(authUser?.id));
                                    toggleAttendance(entry.id, authUser?.id, !att?.attended);
                                  }} />
                                <Text fontSize="10px" color="gray.500">{authUser?.display_name} (me)</Text>
                              </Flex>
                            )}
                            {(entry.users || []).length === 0 && !(attendance[entry.id] || []).length && (
                              <Text fontSize="9px" color="gray.400">No users assigned — mark yourself</Text>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Contact attendance */}
                      {(entry.contacts || []).length > 0 && (
                        <Box mt={1}>
                          <Badge fontSize="8px" colorScheme="purple" cursor="pointer" display="flex" alignItems="center" gap={1}
                            onClick={() => {
                              const key = `c-${entry.id}`;
                              if (expandedAttendance === key) { setExpandedAttendance(null); }
                              else { setExpandedAttendance(key); fetchContactAttendance(entry.id); }
                            }}>
                            <FiUserCheck size={10} /> Contacts {expandedAttendance === `c-${entry.id}` ? '▾' : '▸'}
                          </Badge>
                          {expandedAttendance === `c-${entry.id}` && (
                            <Box mt={1} pl={2} borderLeft="2px" borderColor="purple.200">
                              {(entry.contacts || []).map(ct => {
                                const rec = (contactAttendance[entry.id] || []).find(r => r.contact_id === ct.id);
                                const attended = rec ? !!rec.attended : false;
                                return (
                                  <Flex key={ct.id} align="center" gap={2} py={0.5}>
                                    <Checkbox size="sm" colorScheme="purple" isChecked={attended}
                                      onChange={() => toggleContactAttendance(entry.id, ct.id, !attended)} />
                                    <Text fontSize="10px" color={attended ? 'purple.600' : 'gray.500'}
                                      fontWeight={attended ? '600' : '400'}>
                                      {ct.first_name} {ct.last_name || ''}
                                      {ct.company_name && <Text as="span" color="gray.400"> · {ct.company_name}</Text>}
                                    </Text>
                                    {attended && <Badge fontSize="7px" colorScheme="purple">✓</Badge>}
                                  </Flex>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Complete / Reschedule actions */}
                      <HStack mt={2} spacing={2} flexWrap="wrap">
                        {entry.is_complete ? (
                          <Button size="xs" leftIcon={<FiCheckCircle size={11} />} colorScheme="green" variant="solid"
                            isLoading={savingAction === entry.id}
                            onClick={() => markComplete(entry, false)}>
                            Completed ✓
                          </Button>
                        ) : (
                          <Button size="xs" leftIcon={<FiCheck size={11} />} colorScheme="green" variant="outline"
                            isLoading={savingAction === entry.id}
                            onClick={() => markComplete(entry, true)}>
                            Mark Complete
                          </Button>
                        )}
                        <Button size="xs" leftIcon={<FiCalendar size={11} />} colorScheme="orange" variant="outline"
                          onClick={() => {
                            setExpandedActions(expandedActions === entry.id ? null : entry.id);
                            if (expandedActions !== entry.id) {
                              setRescheduleForm(prev => ({
                                ...prev,
                                [entry.id]: { new_date: entry.start_date?.split('T')[0] || '', new_time: entry.start_time || '', notes: '' }
                              }));
                            }
                          }}>
                          Reschedule
                        </Button>
                      </HStack>

                      {/* Reschedule panel */}
                      {expandedActions === entry.id && (
                        <Box mt={2} p={2} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.200">
                          <Text fontSize="10px" fontWeight="700" color="orange.700" mb={2}>Reschedule Event</Text>
                          {entry.rescheduled_to_date && (
                            <Text fontSize="9px" color="gray.500" mb={1}>
                              Previously rescheduled from: {new Date(entry.rescheduled_to_date).toLocaleDateString('en-GB')}
                              {entry.rescheduled_notes && ` — ${entry.rescheduled_notes}`}
                            </Text>
                          )}
                          <VStack spacing={2} align="stretch">
                            <HStack spacing={2}>
                              <FormControl flex={1}>
                                <FormLabel fontSize="xs" mb={0.5}>New Date</FormLabel>
                                <Input size="xs" type="date" bg="white"
                                  value={rescheduleForm[entry.id]?.new_date || ''}
                                  onChange={e => setRescheduleForm(prev => ({ ...prev, [entry.id]: { ...prev[entry.id], new_date: e.target.value } }))} />
                              </FormControl>
                              <FormControl flex={1}>
                                <FormLabel fontSize="xs" mb={0.5}>New Time</FormLabel>
                                <Input size="xs" type="time" bg="white"
                                  value={rescheduleForm[entry.id]?.new_time || ''}
                                  onChange={e => setRescheduleForm(prev => ({ ...prev, [entry.id]: { ...prev[entry.id], new_time: e.target.value } }))} />
                              </FormControl>
                            </HStack>
                            <FormControl>
                              <FormLabel fontSize="xs" mb={0.5}>Reason / Notes</FormLabel>
                              <Input size="xs" bg="white" placeholder="e.g. Client unavailable"
                                value={rescheduleForm[entry.id]?.notes || ''}
                                onChange={e => setRescheduleForm(prev => ({ ...prev, [entry.id]: { ...prev[entry.id], notes: e.target.value } }))} />
                            </FormControl>
                            <HStack justify="flex-end" spacing={2}>
                              <Button size="xs" variant="ghost" onClick={() => setExpandedActions(null)}>Cancel</Button>
                              <Button size="xs" colorScheme="orange" leftIcon={<FiRefreshCw size={10} />}
                                isLoading={savingAction === entry.id}
                                onClick={() => doReschedule(entry.id)}>
                                Confirm Reschedule
                              </Button>
                            </HStack>
                          </VStack>
                        </Box>
                      )}

                    </Box>
                    <HStack spacing={0}>
                      <IconButton className="hide-btn" icon={isEntryHidden ? <FiEye /> : <FiEyeOff />} size="xs" variant="ghost"
                        colorScheme={isEntryHidden ? 'green' : 'gray'} opacity={isEntryHidden ? 1 : 0} transition="opacity 0.2s"
                        title={isEntryHidden ? 'Show again' : 'Hide'}
                        onClick={async () => {
                          try {
                            if (isEntryHidden) { await api.post(`/calendar/${entry.id}/unhide`); }
                            else { await api.post(`/calendar/${entry.id}/hide`); }
                            await fetchEntries();
                          } catch(e) {}
                        }} aria-label={isEntryHidden ? 'Show' : 'Hide'} />
                      <IconButton icon={<FiCopy />} size="xs" variant="ghost" colorScheme="purple"
                        title="Clone entry" onClick={() => openClone(entry)} aria-label="Clone" />
                      <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(entry)} aria-label="Edit" />
                      {(entry.recurrence_type && entry.recurrence_type !== 'none') || entry.parent_entry_id ? (
                        <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="orange"
                          title="Remove future recurring entries"
                          onClick={async () => {
                            try {
                              await api.delete(`/calendar/${entry.id}/future-recurring`);
                              await fetchEntries();
                              toast({ title: 'Future entries removed', status: 'info', duration: 1500 });
                            } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
                          }} aria-label="Remove Future" />
                      ) : null}
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(entry.id, entry.title)} aria-label="Delete" />
                    </HStack>
                  </Flex>
                </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" justify="space-between">
            <Button size="sm" variant={showHidden ? 'solid' : 'ghost'} colorScheme={showHidden ? 'orange' : 'gray'}
              onClick={() => setShowHidden(!showHidden)}>
              {showHidden ? 'Hide Hidden' : 'Show Hidden'}
              {!showHidden && entries.filter(e => e.is_hidden || e.show_in_schedule === false).length > 0 && (
                <Badge ml={1} colorScheme="orange" fontSize="9px" borderRadius="full">
                  {entries.filter(e => e.is_hidden || e.show_in_schedule === false).length}
                </Badge>
              )}
            </Button>
            <Button size="sm" onClick={onClose}>Close</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}
      title="Delete Calendar Entry" message={`Are you sure you want to delete "${confirmDeleteTitle}"? This cannot be undone.`}
      confirmLabel="Delete" confirmColor="red" onConfirm={doDelete} />
    </>
  );
}
